import React from 'react';

import CloseIcon from '@mui/icons-material/Close';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import fetchTzmmsActivity from './api/tzmmsActivityApi.js';
import TZMMSActivityTable from './components/TZMMSActivityTable.jsx';

const MONTH_COLUMNS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function csvEscape(value) {
  const text = value == null ? '' : String(value);
  const escaped = text.replace(/"/g, '""');
  return `"${escaped}"`;
}

function buildExportCsv(rows, { asCurrency, showPaymentMethod, footerTotals, title }) {
  const columns = [
    'Division',
    'REP',
    '2ndRep',
    '3rdRep',
    'Code',
    'Facility',
    'Service',
    'State',
    'Year',
    ...MONTH_COLUMNS,
    'Total',
  ];

  if (showPaymentMethod) {
    columns.push('Payment Method');
  }

  const lines = [columns];

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const line = [
      row?.Division ?? '',
      row?.REP ?? '',
      row?.['2ndRep'] ?? '',
      row?.['3rdRep'] ?? '',
      row?.Code ?? '',
      row?.Facility ?? '',
      row?.Service ?? '',
      row?.State ?? '',
      row?.Year ?? '',
      ...MONTH_COLUMNS.map((month) => row?.[month] ?? ''),
      row?.Total ?? '',
    ];

    if (showPaymentMethod) {
      line.push(row?.PaymentMethod ?? '');
    }

    lines.push(
      line.map((cell) => (asCurrency && typeof cell === 'number' ? cell : csvEscape(cell)))
    );
  });

  if (footerTotals) {
    const totalLine = [
      'Totals',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      ...MONTH_COLUMNS.map((month) => footerTotals[month] ?? 0),
      footerTotals.Total ?? 0,
    ];

    if (showPaymentMethod) {
      totalLine.push('');
    }

    lines.push(totalLine.map((cell) => csvEscape(cell)));
  }

  const csvText = lines.map((line) => line.join(',')).join('\n');

  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function downloadTableCsv({ title, rows, asCurrency, showPaymentMethod, footerTotals }) {
  buildExportCsv(rows, { asCurrency, showPaymentMethod, footerTotals, title });
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function buildTotals(rows) {
  const totals = { Total: 0 };
  MONTH_COLUMNS.forEach((month) => {
    totals[month] = 0;
  });

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    MONTH_COLUMNS.forEach((month) => {
      totals[month] += toNumber(row?.[month]);
    });
    totals.Total += toNumber(row?.Total);
  });

  return totals;
}

function normalizeYears(rawYears) {
  return (Array.isArray(rawYears) ? rawYears : [])
    .map((year) => Number.parseInt(String(year ?? ''), 10))
    .filter((year) => Number.isFinite(year))
    .sort((a, b) => b - a);
}

function normalizeSearchValue(value) {
  return String(value ?? '')
    .toLowerCase()
    .trim();
}

function matchesSearch(row, normalizedQuery) {
  if (!normalizedQuery) {
    return true;
  }

  return [row?.REP, row?.['2ndRep'], row?.['3rdRep'], row?.Facility, row?.Code, row?.Service].some(
    (value) => normalizeSearchValue(value).includes(normalizedQuery)
  );
}

function TZMMSActivityPage() {
  const [years, setYears] = React.useState([]);
  const [selectedYear, setSelectedYear] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [commissionsPaid, setCommissionsPaid] = React.useState([]);
  const [studiesConducted, setStudiesConducted] = React.useState([]);
  const [studiesTotals, setStudiesTotals] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState('');

  const loadData = React.useCallback(async (requestedYear = null) => {
    setLoading(true);
    setErrorMessage('');

    try {
      const payload = await fetchTzmmsActivity(requestedYear || undefined);
      const nextYears = normalizeYears(payload?.years);

      setYears(nextYears);
      setSelectedYear(String(payload?.selectedYear ?? nextYears[0] ?? ''));
      setCommissionsPaid(Array.isArray(payload?.commissionsPaid) ? payload.commissionsPaid : []);
      setStudiesConducted(Array.isArray(payload?.studiesConducted) ? payload.studiesConducted : []);
      setStudiesTotals(payload?.studiesTotals ?? null);
    } catch (error) {
      setCommissionsPaid([]);
      setStudiesConducted([]);
      setStudiesTotals(null);
      setErrorMessage(
        error?.response?.data?.error || error?.message || 'Failed to load TZMMS Activity'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleYearChange = React.useCallback(
    async (event) => {
      const year = event.target.value;
      setSelectedYear(String(year));
      await loadData(year);
    },
    [loadData]
  );

  const normalizedSearchQuery = React.useMemo(
    () => normalizeSearchValue(searchQuery),
    [searchQuery]
  );

  const filteredCommissionsPaid = React.useMemo(
    () => commissionsPaid.filter((row) => matchesSearch(row, normalizedSearchQuery)),
    [commissionsPaid, normalizedSearchQuery]
  );

  const filteredStudiesConducted = React.useMemo(
    () => studiesConducted.filter((row) => matchesSearch(row, normalizedSearchQuery)),
    [studiesConducted, normalizedSearchQuery]
  );

  const commissionsTotals = React.useMemo(
    () => buildTotals(filteredCommissionsPaid),
    [filteredCommissionsPaid]
  );
  const effectiveStudiesTotals = React.useMemo(
    () =>
      normalizedSearchQuery
        ? buildTotals(filteredStudiesConducted)
        : studiesTotals || buildTotals(studiesConducted),
    [filteredStudiesConducted, normalizedSearchQuery, studiesConducted, studiesTotals]
  );

  return (
    <Box sx={{ mx: '0.25rem', mb: '0.25rem', mt: '1rem' }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography variant="h4">TZMMS Activity</Typography>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="tzmms-activity-year-label">Year</InputLabel>
            <Select
              labelId="tzmms-activity-year-label"
              value={selectedYear}
              label="Year"
              onChange={handleYearChange}
              disabled={loading || years.length === 0}
            >
              {years.map((year) => (
                <MenuItem key={year} value={String(year)}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Search"
            placeholder="Rep, Facility, Code, Service"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            InputProps={{
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear search"
                    size="small"
                    onClick={() => setSearchQuery('')}
                    edge="end"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ minWidth: 280 }}
          />
        </Stack>

        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <TZMMSActivityTable
              title="Commissions Paid on Activity"
              rows={filteredCommissionsPaid}
              showPaymentMethod
              asCurrency
              footerTotals={commissionsTotals}
              onExport={() =>
                downloadTableCsv({
                  title: 'Commissions Paid on Activity',
                  rows: filteredCommissionsPaid,
                  showPaymentMethod: true,
                  asCurrency: true,
                  footerTotals: commissionsTotals,
                })
              }
            />
            <TZMMSActivityTable
              title="Studies Conducted by Type and Account"
              rows={filteredStudiesConducted}
              showPaymentMethod={false}
              asCurrency={false}
              footerTotals={effectiveStudiesTotals}
              onExport={() =>
                downloadTableCsv({
                  title: 'Studies Conducted by Type and Account',
                  rows: filteredStudiesConducted,
                  showPaymentMethod: false,
                  asCurrency: false,
                  footerTotals: effectiveStudiesTotals,
                })
              }
            />
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

export default React.memo(TZMMSActivityPage);
